import type { LonLat } from "./geometry";
import { mercatorProjector } from "../manoeuvre/limaChips";
import { boxesOverlap, type ScreenBox, TAG_HEIGHT_PX, TAG_MARGIN_PX, tagWidthPx } from "../manoeuvre/zoMeasures";

/** Décalage vertical maximal (px) d'une étiquette pour éviter les symboles des unités : au-delà, elle n'évite plus que
 *  les autres étiquettes (elle resterait trop loin de son graphique). */
export const UNIT_AVOID_MAX_PX = 40;
const MAX_STEPS = 32;
/** Nombre d'étiquettes jusqu'auquel tous les ordres de pose sont essayés. */
const ORDER_SEARCH_MAX = 5;

/** Étiquettes des graphiques tactiques sans recouvrement : chaque puce est centrée sur son point ; une puce qui
 *  recouvrirait une puce déjà posée (« OBJ 1 » sous « AXE BLEU ») ou un symbole d'unité (`units` : boîtes centrées sur
 *  leur point, `unitBox` px) se déplace verticalement, vers le haut ou vers le bas, du plus court déplacement qui la
 *  dégage plus 4 px ; sans place libre des unités à moins de `UNIT_AVOID_MAX_PX`, seules les étiquettes comptent. Les
 *  puces se posent l'une après l'autre (les premières gardent leur place) ; jusqu'à 5 étiquettes, l'ordre de pose
 *  retenu est celui qui laisse le moins d'étiquettes sur une unité, puis les décale le moins. Calcul au zoom réel et
 *  au cap de la carte, sur la projection de Mercator de la carte. Rend le décalage vertical (px) de chaque puce, par
 *  identifiant. Fonction pure. */
export function stackGraphicTags(
  tags: readonly { id: string; label: string; at: LonLat }[],
  zoom: number,
  bearing = 0,
  units: readonly LonLat[] = [],
  unitBox: { width: number; height: number } = { width: 64, height: 60 },
): Map<string, number> {
  const project = mercatorProjector(zoom, bearing);
  const unitBoxes: ScreenBox[] = units.map((at) => {
    const [x, y] = project(at);
    return { x0: x - unitBox.width / 2, y0: y - unitBox.height / 2, x1: x + unitBox.width / 2, y1: y + unitBox.height / 2 };
  });
  const boxOf = (tag: (typeof tags)[number]) => {
    const [x, y] = project(tag.at);
    const half = (tagWidthPx(tag.label) + 14) / 2; // pastille de couleur (8 px) et son écart (6 px)
    return (dy: number): ScreenBox => ({ x0: x - half, y0: y + dy - TAG_HEIGHT_PX / 2, x1: x + half, y1: y + dy + TAG_HEIGHT_PX / 2 });
  };
  const boxes = tags.map(boxOf);
  /** Pose dans l'ordre `order` : décalages et coût (puces posées sur une unité, puis décalage total). */
  const placeInOrder = (order: readonly number[]) => {
    const placed: ScreenBox[] = [];
    const dys = new Array<number>(tags.length).fill(0);
    let cost = 0;
    for (const i of order) {
      const boxAt = boxes[i]!;
      /** Plus court décalage libre dans le sens `dir` (+1 : vers le bas, −1 : vers le haut), ou null. */
      const search = (dir: 1 | -1, obstacles: readonly ScreenBox[]): number | null => {
        let dy = 0;
        for (let step = 0; step < MAX_STEPS; step++) {
          const box = boxAt(dy);
          const hits = obstacles.filter((other) => boxesOverlap(box, other));
          if (hits.length === 0) return dy;
          // Dégagement de l'obstacle le plus loin dans ce sens, + 0,01 px contre les restes d'arrondi.
          dy =
            dir > 0
              ? Math.max(...hits.map((other) => dy + other.y1 + TAG_MARGIN_PX - box.y0 + 0.01))
              : Math.min(...hits.map((other) => dy + other.y0 - TAG_MARGIN_PX - box.y1 - 0.01));
        }
        return null;
      };
      const shortest = (obstacles: readonly ScreenBox[]): number | null => {
        const down = search(1, obstacles);
        const up = search(-1, obstacles);
        if (down === null) return up;
        if (up === null) return down;
        return Math.abs(up) < Math.abs(down) ? up : down;
      };
      let dy = unitBoxes.length > 0 ? shortest([...placed, ...unitBoxes]) : null;
      if (dy === null || Math.abs(dy) > UNIT_AVOID_MAX_PX) dy = shortest(placed) ?? 0;
      const box = boxAt(dy);
      placed.push(box);
      dys[i] = dy;
      cost += Math.abs(dy) + 100 * unitBoxes.filter((u) => boxesOverlap(box, u, 0)).length;
    }
    return { dys, cost };
  };
  // Ordre de pose : celui reçu, ou le meilleur de tous (jusqu'à `ORDER_SEARCH_MAX` étiquettes) — une étiquette posée
  // la première ne doit pas en condamner une autre à rester sur une unité.
  const indices = tags.map((_, i) => i);
  let best = placeInOrder(indices);
  if (unitBoxes.length > 0 && tags.length > 1 && tags.length <= ORDER_SEARCH_MAX) {
    for (const order of permutations(indices)) {
      const tried = placeInOrder(order);
      if (tried.cost < best.cost - 0.5) best = tried;
    }
  }
  const out = new Map<string, number>();
  tags.forEach((tag, i) => out.set(tag.id, Math.round(best.dys[i]! * 100) / 100));
  return out;
}

/** Toutes les permutations de `items`. */
function permutations<T>(items: readonly T[]): T[][] {
  if (items.length <= 1) return [[...items]];
  return items.flatMap((item, i) => permutations([...items.slice(0, i), ...items.slice(i + 1)]).map((rest) => [item, ...rest]));
}
